import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import {
  GetStagesRequest,
  Stage,
  StageStatusFilter,
} from "../api/getStages/types";
import { getStagesAction } from "../slice/stageSlice";

export const useStages = () => {
  const dispatch = useAppDispatch();

  const stages = useAppSelector((state: RootState) => state.stage.stages);
  const totalCount = useAppSelector(
    (state: RootState) => state.stage.totalCount,
  );
  const loading = useAppSelector((state: RootState) => state.stage.loadingList);
  const error = useAppSelector((state: RootState) => state.stage.listError);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<StageStatusFilter>("all");

  const [viewingStage, setViewingStage] = useState<Stage | null>(null);

  const requestData = useMemo<GetStagesRequest>(
    () => ({
      searchTerm: searchTerm || undefined,
      status,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting.length
        ? (sorting[0].id as "name" | "description")
        : undefined,
      sortOrder: sorting.length
        ? sorting[0].desc
          ? "DESC"
          : "ASC"
        : undefined,
    }),
    [searchTerm, status, pagination, sorting],
  );

  useEffect(() => {
    dispatch(getStagesAction(requestData));
  }, [dispatch, requestData]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStatusChange = useCallback((nextStatus: StageStatusFilter) => {
    setStatus(nextStatus);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const openView = useCallback((stage: Stage) => setViewingStage(stage), []);
  const closeView = useCallback(() => setViewingStage(null), []);

  return {
    stages,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    searchTerm,
    status,
    handlePaginationChange,
    handleSortingChange,
    handleSearchChange,
    handleStatusChange,

    viewingStage,
    openView,
    closeView,
  };
};
